import React from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { MessageSquare, ArrowRight } from "lucide-react";

interface TrendingDiscussionsSectionProps {
  activeDiscussion: any;
  discussions: any[];
  isLoading: boolean;
}

export function TrendingDiscussionsSection({ activeDiscussion, discussions, isLoading }: TrendingDiscussionsSectionProps) {
  const hasData = activeDiscussion || discussions.length > 0;

  if (!hasData && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="discussions-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Discussions"
          icon={MessageSquare}
          title="Trending Discussions"
          href="/discussions"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : hasData ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {activeDiscussion && (
              <div className="lg:col-span-2">
                <Link href={`/discussions/${activeDiscussion.id}`} className="group block">
                  <Card className="hover:border-neutral-300 transition-all duration-150">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{activeDiscussion.replyCount ?? 0} replies</span>
                          </div>
                          <h3 className="text-lg font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors">
                            {activeDiscussion.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mt-2">
                            {typeof activeDiscussion.content === "string" ? activeDiscussion.content.slice(0, 200) : ""}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            )}
            <div className="space-y-4">
              {discussions.slice(1, 4).map((item: any) => (
                <Link key={item.id} href={`/discussions/${item.id}`} className="group block">
                  <Card className="hover:border-neutral-300 transition-all duration-150">
                    <CardContent className="p-4">
                      <h4 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {item.title}
                      </h4>
                      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                        <MessageSquare className="h-3 w-3" />
                        <span>{item.replyCount ?? 0} replies</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState icon={MessageSquare} description="No discussions yet." />
        )}
      </div>
    </section>
  );
}